import { lazy, Suspense, useCallback, useEffect, useMemo, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SudobilityAppWithFirebaseAuth } from '@sudobility/building_blocks/firebase';
import { variants } from '@sudobility/design';
import {
  AppPageLayout,
  type TopBarConfig,
  type FooterConfig,
  type FooterLinkSection,
  type AuthActionProps,
} from '@sudobility/building_blocks';
import type { MenuItemConfig } from '@sudobility/building_blocks';
import { CreditBalanceBadge } from '@sudobility/consumables_pages';
import { useBalance } from '@sudobility/consumables_client';
import i18n, { supportedLanguages, type SupportedLanguage } from './i18n';
import { API_URL, APP_NAME, APP_DOMAIN, COMPANY_NAME } from './config/constants';
import { AuthProviderWrapper } from './components/providers/AuthProviderWrapper';
import { LinkWrapper } from './components/LinkWrapper';
import { AuthAction, useAuthStatus } from '@sudobility/auth-components';
import { deleteAccount } from '@sudobility/auth_lib';
import { authNetworkClient } from './lib/auth-network-client';
import { BookOpenIcon, ClockIcon, GearIcon, UsersGroupIcon } from './components/icons';
import { SEOHeadProvider } from '@sudobility/seo_lib';
import { seoHeadConfig } from './config/seo';
import { PageConfigProvider } from './context/PageConfigProvider';
import { SignInProvider } from './context/SignInProvider';
import { useSignIn } from './context/sign-in-context';
import { usePageConfig } from './hooks/usePageConfig';
import { FOOTER_USE_CASES, isUseCaseSlug } from './config/use-cases';
import ConvertPage from './pages/ConvertPage';
import SplitPage from './pages/SplitPage';

/*
 * Lazy-loaded page components for route-level code splitting.
 * Only ConvertPage is eagerly imported since it is the primary entry point.
 * All other pages are loaded on demand to reduce the initial bundle size.
 */
const CreditsPage = lazy(() => import('./pages/CreditsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const TutorialsPage = lazy(() => import('./pages/TutorialsPage'));
const TutorialsHubPage = lazy(() => import('./pages/TutorialsHubPage'));
const UseCasePage = lazy(() => import('./pages/UseCasePage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CommunitiesPage = lazy(() => import('./pages/CommunitiesPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div role="status" aria-label="Loading" className={variants.loading.spinner.large()} />
  </div>
);

function LangLayoutInner() {
  const { lang } = useParams<{ lang: string }>();
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { balance, isLoading: balanceLoading } = useBalance();
  const { pageConfig } = usePageConfig();
  const { user } = useAuthStatus();
  const { openSignIn } = useSignIn();

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18nInstance.language !== lang) {
        i18nInstance.changeLanguage(lang);
      }
    } else if (lang) {
      navigate('/en', { replace: true });
    }
  }, [lang, i18nInstance, navigate]);

  const currentLang = (lang || i18nInstance.language || 'en') as string;

  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        id: 'resources',
        label: t('navigation.resources', { defaultValue: 'Resources' }),
        icon: BookOpenIcon,
        href: `/${currentLang}/resources`,
      },
      {
        id: 'tutorials',
        label: t('navigation.tutorials'),
        icon: BookOpenIcon,
        href: `/${currentLang}/tutorials`,
      },
      {
        id: 'communities',
        label: t('navigation.communities', { defaultValue: 'Communities' }),
        icon: UsersGroupIcon,
        href: `/${currentLang}/communities`,
      },
    ];
    items.push({
      id: 'history',
      label: t('navigation.history', { defaultValue: 'History' }),
      icon: ClockIcon,
      href: `/${currentLang}/history`,
    });
    items.push({
      id: 'settings',
      label: t('navigation.settings', { defaultValue: 'Settings' }),
      icon: GearIcon,
      href: `/${currentLang}/settings`,
    });
    return items;
  }, [t, currentLang]);

  const handleDeleteAccount = useCallback(async () => {
    if (!user || user.isAnonymous) return;
    const confirmed = window.confirm(
      t(
        'auth.deleteAccountConfirm',
        'Are you sure you want to permanently delete your account? This cannot be undone.'
      )
    );
    if (!confirmed) return;
    try {
      await deleteAccount({
        networkClient: authNetworkClient,
        baseUrl: API_URL,
        userId: user.uid,
      });
      navigate(`/${currentLang}`, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.deleteAccountFailed');
      window.alert(message);
    }
  }, [user, currentLang, navigate, t]);

  const authenticatedMenuItems = useMemo(
    () => [
      {
        id: 'delete-account',
        label: t('auth.deleteAccount', { defaultValue: 'Delete Account' }),
        onClick: handleDeleteAccount,
      },
    ],
    [t, handleDeleteAccount]
  );

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      if (supportedLanguages.includes(newLang as SupportedLanguage)) {
        i18nInstance.changeLanguage(newLang);
        // Replace the lang prefix in the current path
        const subPath = location.pathname.replace(`/${currentLang}`, '') || '';
        navigate(`/${newLang}${subPath}`);
      }
    },
    [i18nInstance, navigate, location.pathname, currentLang]
  );

  const pathParts = location.pathname.split('/').filter(Boolean);
  const isHomePage = pathParts.length <= 1;

  const linkSections: FooterLinkSection[] = [
    {
      title: t('product', { defaultValue: 'Product' }),
      links: [
        { label: t('convert', { defaultValue: 'Convert' }), href: `/${currentLang}` },
        {
          label: t('navigation.resources', { defaultValue: 'Resources' }),
          href: `/${currentLang}/resources`,
        },
        {
          label: t('navigation.tutorials', { defaultValue: 'Tutorials' }),
          href: `/${currentLang}/tutorials`,
        },
        {
          label: t('navigation.communities', { defaultValue: 'Communities' }),
          href: `/${currentLang}/communities`,
        },
      ],
    },
    {
      title: t('navigation.tutorials', { defaultValue: 'Tutorials' }),
      links: FOOTER_USE_CASES.map(slug => ({
        label: t(`pages.${slug}.card.label`, { ns: 'usecases' }),
        href: `/${currentLang}/tutorials/${slug}`,
      })),
    },
    {
      title: t('legal', { defaultValue: 'Legal' }),
      links: [
        { label: t('privacy'), href: `/${currentLang}/privacy` },
        { label: t('terms'), href: `/${currentLang}/terms` },
        { label: t('sitemap.title', { defaultValue: 'Sitemap' }), href: `/${currentLang}/sitemap` },
      ],
    },
  ];

  const footerConfig: FooterConfig = isHomePage
    ? {
        variant: 'full',
        LinkComponent: LinkWrapper,
        logo: { appName: APP_NAME },
        linkSections,
        companyName: COMPANY_NAME,
        companyUrl: `https://${APP_DOMAIN}`,
        description: t('footerDescription'),
        rightsText: t('allRightsReserved'),
      }
    : {
        variant: 'compact',
        LinkComponent: LinkWrapper,
        companyName: COMPANY_NAME,
        companyUrl: `https://${APP_DOMAIN}`,
        rightsText: t('allRightsReserved'),
        links: [
          { label: t('privacy'), href: `/${currentLang}/privacy` },
          { label: t('terms'), href: `/${currentLang}/terms` },
          {
            label: t('sitemap.title', { defaultValue: 'Sitemap' }),
            href: `/${currentLang}/sitemap`,
          },
        ],
        sticky: true,
      };

  const topBarConfig: TopBarConfig = {
    variant: 'firebase',
    // Without this the library falls back to a plain <a href>, so every menu
    // and footer link becomes a full page reload instead of a route change.
    LinkComponent: LinkWrapper,
    logo: {
      src: '/logo.svg',
      appName: APP_NAME,
      onClick: () => navigate(`/${currentLang}`),
    },
    menuItems,
    currentLanguage: currentLang,
    onLanguageChange: handleLanguageChange,
    renderCenterSection: () => (
      <CreditBalanceBadge
        balance={balance}
        isLoading={balanceLoading}
        onClick={() => navigate(`/${currentLang}/credits`)}
      />
    ),
    AuthActionComponent: AuthAction as ComponentType<AuthActionProps>,
    authenticatedMenuItems,
    // Sign in over the current page (LoginModal); the login route is only for
    // somebody who navigates to it.
    onLoginClick: () => openSignIn(),
    sticky: true,
  };

  return (
    <AppPageLayout
      topBar={topBarConfig}
      footer={footerConfig}
      page={{
        // Use the theme's base surface so retro matches sudobility_design's
        // bg-background shell instead of the darker muted surface.
        background: 'white',
        className: 'h-dvh min-h-0',
        mainClassName: 'flex flex-col',
        // `layoutMode` and `contentClassName` come from `DEFAULT_PAGE_CONFIG`
        // so a page override via `useSetPageConfig` can replace them.
        ...pageConfig,
      }}
    >
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </AppPageLayout>
  );
}

/**
 * Route-level layout providing PageConfigProvider so child pages
 * can use useSetPageConfig for layout overrides, and SignInProvider, which
 * mounts the one LoginModal that the top bar and pages open with `openSignIn`.
 */
function LangLayout() {
  return (
    <PageConfigProvider>
      <SignInProvider>
        <LangLayoutInner />
      </SignInProvider>
    </PageConfigProvider>
  );
}

function LegacyUseCaseRedirect() {
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  return <Navigate to={`/${lang || 'en'}/tutorials/${slug || ''}`} replace />;
}

function TutorialRoute() {
  const { slug } = useParams<{ slug: string }>();
  if (isUseCaseSlug(slug)) return <UseCasePage section="tutorials" />;
  return <TutorialsPage />;
}

function NotFoundRedirect() {
  const { lang } = useParams();
  return <Navigate to={'/' + (lang || 'en') + '/404'} replace />;
}

function NotFoundPage() {
  const { lang } = useParams();
  return (
    <main className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="mb-4 text-4xl font-bold">404</h1>
      <p className="mb-8">Page Not Found</p>
      <a href={'/' + (lang || 'en')}>Go to Home</a>
    </main>
  );
}

function AppRoutes() {
  const { i18n: i18nInstance } = useTranslation();

  return (
    <Routes>
      <Route path="/:lang" element={<LangLayout />}>
        <Route index element={<ConvertPage />} />
        <Route path="split" element={<SplitPage />} />
        <Route path="use-cases" element={<Navigate to="../tutorials" replace />} />
        <Route path="use-cases/:slug" element={<LegacyUseCaseRedirect />} />
        <Route path="tutorials" element={<TutorialsHubPage />} />
        <Route path="tutorials/:slug" element={<TutorialRoute />} />
        <Route path="communities" element={<CommunitiesPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="credits" element={<CreditsPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="sitemap" element={<SitemapPage />} />
        <Route path="404" element={<NotFoundPage />} />
      </Route>
      <Route path="/" element={<Navigate to={`/${i18nInstance.language || 'en'}`} replace />} />
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SudobilityAppWithFirebaseAuth
        i18n={i18n}
        storageKeyPrefix="svgr"
        baseUrl={API_URL}
        AuthProviderWrapper={AuthProviderWrapper}
      >
        <SEOHeadProvider config={seoHeadConfig}>
          <AppRoutes />
        </SEOHeadProvider>
      </SudobilityAppWithFirebaseAuth>
    </QueryClientProvider>
  );
}

export default App;
